import * as _ from 'lodash';

import { AssetSource, JobStatus } from '../enums';
import type { Category, ClipProject, ClipStory, Item, ItemData, SubClip, ThumbnailResourceData } from '../models';
import type { OU } from '../models/Ou';
import { FuelEntityType } from '../services';
import { SchemaType } from '../store/forms/Asset-Editor/SchemaTypeOptions';
import { dateUtils } from './dateUtils';

// Constants
const CUT_OFF_WIDTH = 969;
const TEXT_SCALE_FACTOR = 113; // TEXT_SCALE_FACTOR * Width = scaleX for svg transform
const TIME_SCALES = [
  { key: 'hour', value: 3600 },
  { key: 'quarterhour', value: 900 },
  { key: 'fiveminute', value: 300 },
  { key: 'minute', value: 60 },
  { key: 'quarterminute', value: 15 },
  { key: 'fiveseconds', value: 5 },
  { key: 'second', value: 1 },
];

const STORY_TIME_MATCH_EPSILON = 0.1;

const CLIP_COLOR = [
  '#89FB15',
  '#02FBFA',
  '#f60018',
  '#ff9a00',
  '#BD15FA',
  '#00C200',
  '#ff9a9a',
  '#A26B21',
  '#1652F9',
  '#9FBB3D',
  '#9d74b0',
  '#ffff00',
  '#008080',
  '#DC7EF7',
  // '#800000', // this is a very dark color, retiring it
];

declare interface TickData {
  ticks?: TimePoint[];
  primaryTimeScale?: any;
  estimatedPoints?: number;
}

declare interface TimePoint {
  positionPct?: number; // position in terms of pct (range is negative infinity to positive infinity)
  positionX?: number; // position in terms of pixels based off given total width (range is negative infinity to positive infinity)
  time?: number;
  id?: number;
  order?: number;
  isPrimary?: boolean;
  showText?: boolean;
}

const cliptoolUtils = {
  // calculates the largest and smallest x values within the given region
  getXBounds(start, end, width) {
    const minX = start <= 0 ? 0 : (-1 * start * width) / (end - start);
    const maxX = start >= 1 ? width : width / (end - start) + minX;
    // console.log('getXBounds', { width, start, end, minX, maxX });
    return { minX, maxX };
  },

  // gets the total position pct (between 0 and 1) across the whole entire range given the relative start and end and the positionPct within that
  getTotalPositionPct(start, end, positionPct) {
    const totalPositionPct = positionPct * (end - start) + start;

    // console.log('getTotalPositionPct', { start, end, positionPct, totalPositionPct });
    return totalPositionPct;
  },

  // gets the position pct (between 0 and 1) of a given point in time based on given timeStart and timeEnd
  getPositionPct(timeStart, timeEnd, time) {
    const positionPct = (time - timeStart) / (timeEnd - timeStart);
    return positionPct;
  },

  // Get the horizontal percentage (between 0 and 1) that the current click sits on the given bounding region
  getPositionPctX(container: HTMLElement, clientX: number): number {
    const boundingRect = container.getBoundingClientRect();
    const posX = clientX - boundingRect.left;
    let pos = posX / boundingRect.width;
    if (pos <= 0) {
      pos = 0;
    } else if (pos >= 1) {
      pos = 1;
    }
    return pos;
  },
  // Get the vertical percentage (between 0 and 1) that the current click sits on the given bounding region
  getPositionPctY(container: HTMLElement, clientY: number): number {
    const boundingRect = container.getBoundingClientRect();
    const posY = clientY - boundingRect.top;
    let pos = posY / boundingRect.height;
    if (pos <= 0) {
      pos = 0;
    } else if (pos >= 1) {
      pos = 1;
    }
    return pos;
  },

  getSelection() {
    if (window.getSelection) {
      return window.getSelection();
    }
    if (document.selection) {
      // IE?
      return document.selection;
    }
  },

  clearSelection() {
    if (window.getSelection) {
      if (window.getSelection().empty) {
        // Chrome
        window.getSelection().empty();
      } else if (window.getSelection().removeAllRanges) {
        // Firefox
        window.getSelection().removeAllRanges();
      }
    } else if (document.selection) {
      // IE?
      document.selection.empty();
    }
  },

  makeDraggable(svg, onDragged, onDragEnded) {
    if (svg) {
      let originalEvt = null;
      let didDrag = false;
      const isCancelled = false;

      const throttledCallback = _.throttle(function (evt) {
        if (onDragged) onDragged(evt, svg, originalEvt);
      }, 60);

      const endDrag = (evt) => {
        // console.log('mouseup', { didDrag });
        // document.removeEventListener('mousemove');
        // document.removeEventListener('mouseup');
        document.removeEventListener('mousemove', drag);
        document.removeEventListener('mouseup', endDrag);
        if (didDrag === true && onDragEnded) onDragEnded(evt, svg, originalEvt);
        originalEvt = null; // clear it out
        didDrag = false;
      };

      const drag = (evt) => {
        didDrag = true;
        throttledCallback(evt);
        evt.preventDefault();
      };

      const startDrag = (evt) => {
        originalEvt = evt;

        document.removeEventListener('mousemove', drag);
        document.removeEventListener('mouseup', endDrag);

        document.addEventListener('mouseup', endDrag);
        document.addEventListener('mousemove', drag);

        // setTimeout(() => {
        //   document.addEventListener('mousemove', drag);
        // }, 100);
      };

      // var svg = evt.target;
      svg.removeEventListener('mousedown', startDrag);

      setTimeout(() => {
        svg.addEventListener('mousedown', startDrag);
      }, 0);

      return startDrag;
    }
  },

  // Get the most significant time point in a span. eg. hour, quarterhour(3), fiveminute(2), minute(4), quarterminute(3), fiveseconds(2), second(4), tenframes(2), frame(9)
  //   timeStart = start time in seconds              eg. 10.58765
  //   timeEnd   =   end time in seconds              eg. 21.7853
  //   level     = index from the TIME_SCALES above.  eg. 2
  getTimePoints(timeStart, timeEnd, level): TimePoint[] {
    const timePoints = [];
    const timeValue = TIME_SCALES[level].value;
    let start;
    let end;
    if (timeValue >= 1) {
      // Dealing with whole numbers of seconds
      start = Math.floor(timeStart / timeValue);
      end = Math.floor(timeEnd / timeValue);
    }
    if (end > start) {
      start = start <= 0 ? 0 : start;
      for (let i = start; i <= end; i++) {
        const time = timeValue * i;
        const position = cliptoolUtils.getPositionPct(timeStart, timeEnd, time);
        timePoints.push({ positionPct: position, time });
      }
    }
    return timePoints;
  },

  // Get the tick marks to display.  eg. [{position:5, time:10.0000},{position:10, time:15.0000},...]
  //   start = zoomstart on 0-100 scale. eg. 52.323
  //   end     = zoomend on 0-100 scale  eg. 100
  //   duration = total video duration in seconds eg. 3654.258
  getTickData(startPct, endPct, duration, width): TickData {
    const tickData = {
      ticks: [],
      primaryTimeScale: null,
      estimatedPoints: 0,
    };
    if (duration > 0) {
      const timeStart = startPct * duration;
      const timeEnd = endPct * duration;
      const timeDiff = timeEnd - timeStart;
      let id = 0;
      let startIndex = -1;
      for (let i = 0; i < TIME_SCALES.length; i++) {
        const timeScale = TIME_SCALES[i];
        const estimatedPoints = timeDiff / timeScale.value;
        if (estimatedPoints > 3 || i >= 6) {
          startIndex = i;
          tickData.primaryTimeScale = timeScale;
          tickData.estimatedPoints = estimatedPoints;
          break;
        }
      }
      // adjust if diff very small
      let count = 0;
      if (startIndex > -1) {
        for (let i = startIndex; i <= startIndex + 1; i++) {
          if (i >= TIME_SCALES.length) {
            break;
          }
          const timePoints = cliptoolUtils.getTimePoints(timeStart, timeEnd, i);
          timePoints.map(function (timePoint) {
            let found = false;
            for (let j = 0; j < tickData.ticks.length; j++) {
              if (tickData.ticks[j].time === timePoint.time) {
                found = true;
                break;
              }
            }
            if (!found) {
              timePoint.id = id++;
              timePoint.order = i;
              timePoint.isPrimary = i == startIndex;
              timePoint.showText = timePoint.isPrimary;
              if (timePoint.showText && timePoints.length > 5 && width < 1000) {
                timePoint.showText = timePoint.showText && count % 2 === 0;
              }
              tickData.ticks.push(timePoint);
              count++;
            }
          });
        }
      }
    }
    const noPrimary = tickData.ticks.filter((t) => t.is);
    return tickData;
  },

  /**
   * Return default clip color
   *
   * @returns {string}
   */
  getDefaultClipColor(): string {
    return CLIP_COLOR[0];
  },
  /**
   * Publish subclips should not be editable, this will return true or false if the subclip is published or not.
   * @param subClip a specific subclip to check if published.
   * @returns  true or false whether the subclip is published.
   */
  isSubClipPublished(subClip: SubClip) {
    if (!subClip || !subClip.item) return false;
    if (subClip.item.data.ingestStatus === JobStatus.Unknown && subClip.item.sourceFileUrl) return true;
    return subClip.item.data.ingestStatus != JobStatus.Unknown;
  },

  startTimeEndTimeformatCheck(value) {
    // console.log('value startTimeEndTimeformatCheck',value, /\d\d:\d\d:\d\d;\d\d/.test(value))
    if (/\d\d:\d\d:\d\d;\d\d/.test(value)) {
      return true;
    }
    return false;
  },

  getFileImageBlob(file) {
    // console.log('getFileImageBlob', { file });
    if (file == null) {
      return null;
    }
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file); // or readAsText, readAsDataURL
    });
  },

  getVideoThumbnail(file, time = 2) {
    // console.log('getVideoThumbnail', { file });
    if (file == null) {
      return null;
    }
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.muted = true;
      video.addEventListener('loadeddata', () => {
        video.currentTime = time;
      });
      video.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg');
        resolve(dataUrl);
      });
      video.addEventListener('error', (error) => {
        reject(error);
      });
      video.src = URL.createObjectURL(file);
    });
  },

  /**
   * Gets the thumbnail for a subclip to the frame that the video is currently on.
   * @param videoElementId the id for the html video element.
   * @param canvasId the id for the html canvas element.
   * @returns  src string for the new thumbnail.
   */
  getClipThumbnail(videoElementId: string = 'clipperPlayer1', canvasId: string = 'thumbnailCanvas'): string {
    // clipperplayer1
    // mycanvas
    // console.log('getClipThumbnail', { videoElementId, canvasId });
    const elements = document.getElementsByTagName('video');
    if (elements && elements.length > 0) {
      const video = elements[0] as HTMLVideoElement;
      const body = document.getElementsByTagName('body')[0];
      const canvas0 = document.getElementById(canvasId) as HTMLCanvasElement;
      if (canvas0) {
        body.removeChild(canvas0);
      }
      const canvasE: HTMLCanvasElement = document.createElement('canvas');
      canvasE.id = canvasId;
      canvasE.className = 'invisible';
      body.appendChild(canvasE);
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL();
        body.removeChild(canvasE);
        return dataURL;
      }
    }
    return '/static/images/thumbnail.png';
  },
  /**
   * When saving the clip project we save the subclip thumbnails separately, this function grabs
   * all subclips that contain base64 image data.
   * @param subClips a list of subclips to save their thumbnails.
   * @returns  a list of subclips without the thumbanail data.
   */
  getSubClipsWithBase64Thumbnails(subClips: SubClip[]) {
    if (!subClips || !subClips.length) return;
    const results = subClips.reduce((base64Items: SubClip[], currentSubClip: SubClip) => {
      if (currentSubClip && currentSubClip.item && currentSubClip.item.data && currentSubClip.item.data.thumbanail) {
        const imageData = currentSubClip.item.data.thumbnail.previewUri;
        try {
          window.atob(imageData.split(',')[1]); // will fail if it's not a base64 string.
          base64Items.push(currentSubClip);
        } catch (e) {}
        return base64Items;
      }
      return null;
    }, []);
    return results;
  },
  /**
   * Makes the call to update subclips thumbnail, typically after the project is saved.
   * since a base64 image string is big we only update subclips that have thumbnails that
   * were changed.
   * @param subClips a list of subclips to save their thumbnails.
   * @returns  a list of subclips without the thumbanail data.
   */
  //  async updateSubClipsItemThumbnail(subClips: SubClip[]) {
  //     if (!subClips || !subClips.length) return;
  //     const thumbnailPromises = subClips.map(async (subClip: SubClip) => {
  //       const { item } = subClip;
  //       // We only want to save thumbnail for subclips that have changed before
  //       if (subClip.thumbnailChanged) {
  //         await ItemsService.saveThumbnail(item.id, item.data.thumbnail.previewUri);
  //         subClip.thumbnailChanged = false;
  //       }
  //     });
  //     return await Promise.all(thumbnailPromises);
  //   },

  /**
   * Removes image data from a list of subclips passed in. This is typically used
   * to remove image data from the clip project before it gets saved by the API
   * since we have a separate API call to save image data.
   * @param subClips a list of subclips remove image data..
   */
  removeItemImageData(subClips: SubClip[]) {
    subClips.map((subClip: SubClip) => {
      if (subClip && subClip.item && subClip.item.data && subClip.item.data.thumbnail)
        subClip.item.data.thumbnail.previewUri = null;
    });
  },

  /**
   * Returns a subclip that is a copy of the clipProject base item
   * and runs from the given videoTime to the end of the video.
   * @param state the ClipperState in the clipper store. (see ClipperStore for information)
   * @param videoTime the current position of the playhead in the video player. (eg. 4.5674 seconds)
   */
  getNewClip(clipProject: ClipProject, startTimeIn: number, endTimeIn: number, userId: string, ouData: OU): SubClip {
    // console.log('getNewClip', { clipProject, startTimeIn, endTimeIn, userId, ouData });
    // Default start and end
    const startTime = startTimeIn || 0;
    const endTime = endTimeIn || 0;
    // Generate a new item that the subclip will wrap
    const item = this.copyItem(clipProject.item, userId, startTime, endTime, ouData);
    item.clipVersion = 'v2';

    let highestOrdinality = 0;
    let nextOrdinality = 0;
    const maxOrdinalitySubclip = _.maxBy(clipProject.subClips, 'ordinality');
    if (maxOrdinalitySubclip != null) {
      highestOrdinality = maxOrdinalitySubclip.ordinality;
      nextOrdinality = highestOrdinality + 1;
    }

    // Instantiate a new subclip
    const newClip: SubClip = {
      startTime,
      endTime,
      item,
      id: null,
      projectId: clipProject.id,
      ordinality: nextOrdinality,
      itemId: null, // the item id will be the same as the subclipid
      clipColor: this.getSubClipColor(clipProject),
      isDeleted: false,
      isHidden: false,
      isDraft: true,
      thumbnailChanged: true, // when creating a new subclip we pass thumbnail data.
      robustClipping: false,
    };
    // console.log(newClip);
    return newClip;
  },
  copyItem(item: Item, userId: string, startTime: number, endTime: number, ouData: OU) {
    // console.log('copyItem', { item, userId, startTime, endTime, ouData });
    const copy: Item = _.cloneDeep(item);
    const itemData: ItemData = this.copyItemData(copy, startTime, endTime, ouData);
    const newItem: Item = {
      // ModelBase Properties
      id: null,
      sourceFileUrl: null,
      applyAutomatedTransitions: true, // aalt 2023-07-28 444205 Enabled Automated Transitions for All New Subclips
      ouId: copy.ouId,
      ownerId: copy.ownerId,
      // createdAt: null,
      createdBy: userId,
      // modifiedAt: null,
      // modifiedBy: null,
      // Item specific properties
      ouName: copy.ouName,
      schemaName: SchemaType.Video,
      schemaType: SchemaType.Video,
      title: itemData.businessMetadata.title,
      data: itemData,
      fileUri: null,
      zipcode: copy.zipcode,
      previewUri: null,
      onDeleteCallback: null,
      attributes: copy.attributes,
      categories: copy.categories,
      tags: copy.tags,
      parentId: copy.id,
      source: AssetSource.SubClip,
      sourceType: 1,
      entityType: FuelEntityType.Clip,
      publicationDate: itemData.businessMetadata.publicationDate,
    };
    return newItem;
  },
  copyItemData(copy: Item, startTime: number, endTime: number, ouData: OU) {
    // console.log('copyItemData', { copy, startTime, endTime, ouData });
    const newBusinessMetadata = copy.data
      ? copy.data.businessMetadata
      : {
          embargoDate: null,
          expireDate: null,
          title: null,
          automatedTitle: null,
          synopsis: null,
        };

    // aalt 2023-01-09 use dateUtils for formatting dates
    newBusinessMetadata.publicationDate = dateUtils.getTimestamp(ouData.timezone);
    // newBusinessMetadata.publicationDate = clientUtils.formatDateTimeToUTC(new Date().toUTCString());
    // set UI override properties
    newBusinessMetadata.embargoDateOverride = newBusinessMetadata.embargoDate == null;
    newBusinessMetadata.expireDateOverride = newBusinessMetadata.expireDate == null;
    const newThumbnail: ThumbnailResourceData = {
      fileUri: null,
      tempThumbnailBlob: this.getClipThumbnail(),
      setByUser: true,
    };
    const data: ItemData = {
      accessSettings: copy.data.accessSettings,
      originalFileName: null,
      businessMetadata: newBusinessMetadata,
      thumbnail: newThumbnail,
      ingestStatus: JobStatus.Unknown,
      subclipInfo: {
        ordinality: 0,
        startTime,
        endTime,
        clipColor: '',
      },
    };
    return data;
  },

  getSubClipColor(clipProject: ClipProject) {
    // default to first color if all else fails
    let clipColor = CLIP_COLOR[0];
    const { subClips } = clipProject;

    let highestOrdinality = 0;
    let nextOrdinality = 0;
    const maxOrdinalitySubclip = _.maxBy(subClips, 'ordinality');
    if (maxOrdinalitySubclip != null) {
      highestOrdinality = maxOrdinalitySubclip.ordinality;
      nextOrdinality = highestOrdinality + 1;
    }

    // Get the next color in the sequence, starting over if necessary. % = modulus
    if (highestOrdinality > -1) {
      clipColor = CLIP_COLOR[nextOrdinality % CLIP_COLOR.length];
    }

    return clipColor;
  },

  categoriesFromNames(names: string[] | undefined, allCategories: Category[]): Category[] {
    if (!names?.length) {
      return [];
    }
    return names.map((name) => {
      const found = allCategories?.find((c) => c.name === name);
      return found ?? ({ id: name, name } as Category);
    });
  },

  showsAiGeneratedBadge(subclip: SubClip | null | undefined): boolean {
    if (!subclip) {
      return false;
    }
    if (subclip.isStoryRecommendation || subclip.isAiGeneratedSubclip || subclip.autoClip === true) {
      return true;
    }
    const subclipInfo = subclip.item?.data?.subclipInfo;
    return Boolean(subclipInfo?.aiStoryId) || subclipInfo?.storyRecommendationIndex != null;
  },

  getStoryIdFromSubclip(subclip: SubClip | null | undefined): string | undefined {
    if (!subclip) {
      return undefined;
    }
    const storyIndex =
      subclip.storyRecommendationIndex ?? subclip.item?.data?.subclipInfo?.storyRecommendationIndex;
    if (storyIndex == null) {
      return undefined;
    }
    return `story-${storyIndex}`;
  },

  storyMatchesSubclip(subclip: SubClip, story: ClipStory): boolean {
    if (!subclip || !story) {
      return false;
    }
    const startMatch = Math.abs(subclip.startTime - story.startTimeSeconds) <= STORY_TIME_MATCH_EPSILON;
    const endMatch = Math.abs(subclip.endTime - story.endTimeSeconds) <= STORY_TIME_MATCH_EPSILON;
    return startMatch && endMatch;
  },

  isStoryIndexMaterialized(storyIndex: number, persistedSubclips: SubClip[], stories: ClipStory[]): boolean {
    const story = stories?.[storyIndex];
    if (!story) {
      return true;
    }
    return persistedSubclips.some((subclip) => {
      const savedStoryId = subclip.item?.data?.subclipInfo?.aiStoryId;
      if (story.id && savedStoryId === story.id) {
        return true;
      }
      if (subclip.storyRecommendationIndex === storyIndex) {
        return true;
      }
      if (subclip.item?.data?.subclipInfo?.storyRecommendationIndex === storyIndex) {
        return true;
      }
      return cliptoolUtils.storyMatchesSubclip(subclip, story);
    });
  },

  buildStoryRecommendationSubclips(
    clipProject: ClipProject,
    stories: ClipStory[] | undefined,
    userId: string,
    ouData: OU,
    persistedSubclips: SubClip[]
  ): SubClip[] {
    if (!stories?.length || !clipProject?.item) {
      return [];
    }
    const virtualSubclips: SubClip[] = [];
    const tempProject: ClipProject = { ...clipProject, subClips: [...persistedSubclips, ...virtualSubclips] };

    for (let storyIndex = 0; storyIndex < stories.length; storyIndex++) {
      if (cliptoolUtils.isStoryIndexMaterialized(storyIndex, persistedSubclips, stories)) {
        continue;
      }
      const story = stories[storyIndex];
      const subclip = cliptoolUtils.getNewClip(tempProject, story.startTimeSeconds, story.endTimeSeconds, userId, ouData);
      const { item } = subclip;
      if (item?.data?.businessMetadata) {
        item.data.businessMetadata.title = story.title ?? '';
        item.data.businessMetadata.synopsis = story.synopsis ?? story.topicSummary ?? '';
      }
      item.title = story.title ?? item.title;
      item.tags = story.tags ?? [];
      item.categories = cliptoolUtils.categoriesFromNames(story.categories, ouData?.categories ?? []);
      if (item.data?.subclipInfo) {
        item.data.subclipInfo.storyRecommendationIndex = storyIndex;
        if (story.id) {
          item.data.subclipInfo.aiStoryId = story.id;
        }
      }
      subclip.isStoryRecommendation = true;
      subclip.isAiGeneratedSubclip = true;
      subclip.storyRecommendationIndex = storyIndex;
      subclip.autoClip = true;
      subclip.isDraft = true;
      subclip.okToPublish = false;
      virtualSubclips.push(subclip);
      tempProject.subClips = [...persistedSubclips, ...virtualSubclips];
    }
    return virtualSubclips;
  },

  sortPersistedSubclips(subclips: SubClip[]): SubClip[] {
    return _.orderBy(subclips, [(s: SubClip) => s.item?.createdDate ?? ''], ['asc']);
  },

  mergeSubclipLists(virtualStorySubclips: SubClip[], persistedSubclips: SubClip[]): SubClip[] {
    return [...persistedSubclips, ...virtualStorySubclips];
  },

  recomputeOrdinalities(subclips: SubClip[]): SubClip[] {
    return subclips.map((subclip, index) => {
      const next = { ...subclip, ordinality: index };
      if (next.item?.data?.subclipInfo) {
        next.item.data.subclipInfo.ordinality = index;
      }
      return next;
    });
  },
};

export { cliptoolUtils, CUT_OFF_WIDTH, TEXT_SCALE_FACTOR, TickData, TIME_SCALES, TimePoint };
